import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxTextBoxModule, DxValidatorModule, DxPopoverModule } from 'devextreme-angular';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { SvgIconComponent } from '../../../../../../../../components/svg-icon/svg-icon.component';

import validationEngine from 'devextreme/ui/validation_engine';
import { ProjectConfigNodeComponent } from '../../project-config-node.component';
import { Phase, TaskPackage } from '../../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-task-package-project-config-node',
    imports: [
        CommonModule,
        FormsModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        DxPopoverModule,
        SvgIconComponent,
        ProjectConfigNodeComponent,
    ],
    templateUrl: './task-package-project-config-node.component.html',
    styleUrl: './task-package-project-config-node.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskPackageProjectConfigNodeComponent {
  @Input() taskPackage!: TaskPackage;
  @Input() phases!: Phase[];
  @Input() itemNumber!: number;
  @Input() isEditing: boolean = false;
  @Input() isNew: boolean = false;
  @Input() isSelected: boolean = false;
  @Input() allowEdit: boolean = true;
  @Input() validationCallback?: (value: string) => boolean;

  @Output() onSave = new EventEmitter<TaskPackage>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onDelete = new EventEmitter<void>();
  @Output() onStartEdit = new EventEmitter<TaskPackage>();
  @Output() onTaskPackageClick = new EventEmitter<void>();
  @Output() hovering = new EventEmitter<boolean>();
  @Output() onArchive = new EventEmitter<void>();
  @Output() onReactivate = new EventEmitter<void>();

  get isArchived(): boolean { return !!this.taskPackage?.archived; }
  get allowArchivePackage(): boolean { return !this.isArchived && (this.allowEdit ?? false); }

  editData: TaskPackage | null = null;

  ngOnInit() {
    this.editData = { ...this.taskPackage };
  }

  // Custom save handler to emit the current task package name
  handleSave() {
    this.onSave.emit(this.editData);
  }

  // Unique name validation for task packages across all phases and milestones
  uniqueNameValidationCallback = (params: any): boolean => {
    const currentName = params?.value || params?.data || params;

    if (!currentName || currentName.trim() === '') {
      return true; // Let required validation handle empty values
    }

    // Check if any other task package across all phases and milestones has the same name (case-insensitive)
    const duplicateExists = this.phases.some(phase => {
      return phase.milestones?.some(milestone => {
        return milestone.taskPackages?.some(otherTaskPackage => {
          if (otherTaskPackage.id === this.taskPackage.id) {
            return false; // Skip current task package
          }

          return (
            otherTaskPackage.name && otherTaskPackage.name.trim().toLowerCase() === currentName.trim().toLowerCase()
          );
        });
      });
    });

    return !duplicateExists;
  };

  onItemClick() {
    if (!this.isEditing) {
      this.onTaskPackageClick.emit();
    }
  }

  onEditing() {
    this.editData = { ...this.taskPackage };
    this.onStartEdit.emit(this.editData!);
  }
}
